import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Package, ChevronRight } from "lucide-react";

const statusColors = {
  pending: "bg-marigold/20 text-deepbrown",
  confirmed: "bg-emerald/20 text-emerald",
  shipped: "bg-rani/20 text-rani",
  delivered: "bg-emerald text-white",
  cancelled: "bg-deepbrown/20 text-deepbrown/60",
};

export default function Account() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  if (authLoading) return <div className="text-center py-20 font-display text-2xl text-deepbrown/60">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="account-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold">My account</p>
          <h1 className="font-display text-4xl sm:text-5xl text-deepbrown font-bold mt-2">Hi, {user.name.split(" ")[0]}!</h1>
          <p className="text-deepbrown/60 mt-1">{user.email}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="px-5 py-2 rounded-full border-2 border-deepbrown text-deepbrown text-sm font-bold hover:bg-deepbrown hover:text-ivory" data-testid="account-logout">
          Logout
        </button>
      </div>

      {/* Quick links */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
        <Link to="/wishlist" className="bg-white rounded-2xl p-4 border border-deepbrown/10 hover:border-rani transition" data-testid="quick-wishlist">
          <p className="text-xs uppercase tracking-widest font-bold text-deepbrown/60">Wishlist</p>
          <p className="font-display text-2xl text-deepbrown">View favorites →</p>
        </Link>
        <Link to="/addresses" className="bg-white rounded-2xl p-4 border border-deepbrown/10 hover:border-rani transition" data-testid="quick-addresses">
          <p className="text-xs uppercase tracking-widest font-bold text-deepbrown/60">Address Book</p>
          <p className="font-display text-2xl text-deepbrown">Manage →</p>
        </Link>
        <Link to="/shop" className="bg-marigold/20 rounded-2xl p-4 border border-marigold/40 hover:border-rani transition" data-testid="quick-shop">
          <p className="text-xs uppercase tracking-widest font-bold text-deepbrown/60">Sutrakosh</p>
          <p className="font-display text-2xl text-deepbrown">Keep shopping →</p>
        </Link>
      </div>

      <h2 className="font-display text-2xl text-deepbrown mt-10">Your Orders</h2>
      {loading ? (
        <p className="text-deepbrown/60 mt-4">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 mt-4 text-center border border-deepbrown/10">
          <Package className="h-12 w-12 text-rani mx-auto mb-3" />
          <p className="font-display text-xl text-deepbrown">No orders yet</p>
          <Link to="/shop" className="inline-block mt-4 px-6 py-2 rounded-full bg-rani text-white font-bold">Shop Now</Link>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {orders.map((o) => (
            <Link key={o.id} to={`/orders/${o.id}`} className="block bg-white rounded-2xl p-5 border border-deepbrown/10 hover:border-rani transition-colors" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-display text-lg text-deepbrown">Order {o.order_number}</p>
                  <p className="text-xs text-deepbrown/60">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.items.length} item(s)</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${statusColors[o.status]}`}>{o.status}</span>
                  <p className="font-display text-xl text-rani font-bold">₹{o.total.toLocaleString("en-IN")}</p>
                  <ChevronRight className="h-5 w-5 text-deepbrown/40" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
